/**
 * @fileoverview AR-805 — re-fetches Classroom state when the user comes back
 * to the tab.
 *
 * The real flow leaves the app: the teacher creates the course, goes to
 * classroom.google.com to accept the invite / check the class, then returns.
 * The prototype solved this with `router.refresh()` on the server component;
 * in this SPA the equivalent is refreshing on window focus, so the card is
 * never stale without asking the user to reload.
 *
 * A minimum interval keeps alt-tabbing from hammering the API.
 */

import { useEffect, useRef } from 'react'

const MIN_INTERVAL_MS = 10_000

export function useRefreshOnFocus(refresh: () => void, enabled = true): void {
  const lastRun = useRef(0)
  // Keeps the effect from re-subscribing on every render when the caller
  // passes an inline function.
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh

  useEffect(() => {
    if (!enabled) return

    const maybeRefresh = () => {
      if (document.visibilityState === 'hidden') return
      const now = Date.now()
      if (now - lastRun.current < MIN_INTERVAL_MS) return
      lastRun.current = now
      refreshRef.current()
    }

    window.addEventListener('focus', maybeRefresh)
    document.addEventListener('visibilitychange', maybeRefresh)
    return () => {
      window.removeEventListener('focus', maybeRefresh)
      document.removeEventListener('visibilitychange', maybeRefresh)
    }
  }, [enabled])
}
